import { RiUserFill } from '@remixicon/react';
import { AlertCircle, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';

interface AuthenticationCardProps {
  onSubmit: (email: string, password: string, keepLoggedIn: boolean) => Promise<void>;
  onGitHubLogin?: () => void;
  onForgotPassword?: () => void;
  error?: string;
  isLoading?: boolean;
}

export const AuthenticationCard = ({
  onSubmit,
  onGitHubLogin,
  onForgotPassword,
  error,
  isLoading = false,
}: AuthenticationCardProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [keepLoggedIn, setKeepLoggedIn] = useState<boolean>(false);

  const toggleVisibility = () => setIsVisible((prevState) => !prevState);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(email, password, keepLoggedIn);
  };

  return (
    <div className="w-full max-w-lg bg-white border border-gray-200 rounded-xl shadow-lg p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4 md:w-20 md:h-20">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border-2 border-blue-200 shadow-sm md:w-14 md:h-14">
            <RiUserFill className="w-6 h-6 text-gray-800 md:w-7 md:h-7" />
          </div>
        </div>

        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">Sign in to your account</h1>
          <p className="text-sm text-gray-600">Enter your credentials to access your account.</p>
        </div>
      </div>

      <div className="h-px bg-gray-200 mb-6"></div>

      <div>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your password"
                type={isVisible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-r-lg transition-colors"
                type="button"
                onClick={toggleVisibility}
                aria-label={isVisible ? 'Hide password' : 'Show password'}
                aria-pressed={isVisible}
                aria-controls="password"
              >
                {isVisible ? (
                  <EyeOffIcon className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <EyeIcon className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="keep-me-logged-in"
                className="w-4 h-4 text-blue-600 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                checked={keepLoggedIn}
                onChange={(e) => setKeepLoggedIn(e.target.checked)}
              />
              <label htmlFor="keep-me-logged-in" className="text-sm font-medium text-gray-700 cursor-pointer">
                Keep me logged in
              </label>
            </div>
            {onForgotPassword && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-700 focus:outline-none focus:underline transition-colors"
                onClick={onForgotPassword}
              >
                Forgot password?
              </button>
            )}
          </div>

          <button
            type="submit"
            className="w-full mt-6 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Continue'}
          </button>

          {onGitHubLogin && (
            <button
              type="button"
              className="w-full px-4 py-2 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={onGitHubLogin}
              disabled={isLoading}
            >
              Sign in with GitHub
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

// Keep the original demo component for reference
export const Component = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const toggleVisibility = () => setIsVisible((prevState) => !prevState);

  return (
    <div className="auth-card">
      <div className="auth-card-header">
        <div className="auth-avatar">
          <div className="auth-avatar-inner">
            <RiUserFill className="icon-lg" style={{ color: 'var(--color-primary)' }} />
          </div>
        </div>

        <div>
          <h1 className="auth-title">Sign in to your account</h1>
          <p className="auth-description">Enter your credentials to access your account.</p>
        </div>
      </div>

      <div className="auth-separator"></div>

      <form className="auth-form">
        <div className="form-group">
          <label htmlFor="demo-email" className="form-label">
            Email
          </label>
          <input id="demo-email" type="email" placeholder="Enter your email" className="form-input" />
        </div>

        <div className="form-group">
          <label htmlFor="demo-password" className="form-label">
            Password
          </label>
          <div className="password-input-wrapper">
            <input
              id="demo-password"
              className="form-input"
              style={{ paddingRight: '2.5rem' }}
              placeholder="Enter your password"
              type={isVisible ? 'text' : 'password'}
            />
            <button
              className="password-toggle-btn"
              type="button"
              onClick={toggleVisibility}
              aria-label={isVisible ? 'Hide password' : 'Show password'}
              aria-pressed={isVisible}
              aria-controls="demo-password"
            >
              {isVisible ? (
                <EyeOffIcon className="icon" aria-hidden="true" />
              ) : (
                <EyeIcon className="icon" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <div className="checkbox-wrapper">
          <div className="checkbox-group">
            <input type="checkbox" id="demo-keep-logged-in" className="checkbox" />
            <label htmlFor="demo-keep-logged-in" className="form-label cursor-pointer">
              Keep me logged in
            </label>
          </div>
          <button type="button" className="btn btn-link btn-sm">
            Forgot password?
          </button>
        </div>

        <button type="button" className="btn btn-primary w-full mt-2">
          Continue
        </button>

        <button type="button" className="btn btn-outline w-full">
          Sign in with GitHub
        </button>
      </form>
    </div>
  );
};
